// ============================================
// CENTRALIZED SELECTORS
// ============================================
// This file exports all selectors used throughout the application.
// Provider-specific selectors come from providerInterface.js, which
// accesses state.game and state.spin (populated by store.js based on provider).
// Shared selectors are exported directly from their respective slices.

// ============================================
// PROVIDER-SPECIFIC SELECTORS
// ============================================
// These work with any provider because store.js loads the correct slice
export {
  selectIsSpinning,
  selectSpinComplete,
  selectWindupComplete,
  selectReelsFrozen,
  selectManualStop,
  selectSessionExpired,
  selectCurrentRound,
  selectWinningLines,
  selectScatterCount,
  selectScatterPositions,
  selectTriggerWin,
  selectWinType,
  selectBalance,
  selectBet,
  selectPaytable,
  selectBaseBet,
  selectCurrency,
  selectCurrencyConfig,
  selectBonusBuyMultiplier,
  selectBonusBuyExtraMultiplier,
  selectBonusBuyMultipliers,
  selectBonusBuyFeatureName,
  selectBonusBuyFeatures,
  selectDisplayedBalance,
  selectBets,
  selectGameStatus,
  selectAssetsStatus,
  selectGameMode,
  selectFeatureState,
  selectReloadState,
  selectAvailableActions,
  selectFlowState,
  selectFeatures,
  selectLastCommand,
  selectErrorCode,
} from "./Slices/providerInterface";

// Universal feature active selector — single source of truth
// gameMode is set to "feature" by RGS whenever any feature is active (bonus, H&W, etc.)
export const selectIsFeatureActive = (state) => state.game.gameMode === "feature";

// Feature game selectors
export const selectHoldAndWinActive = (state) =>
  state.featureGame.holdAndWinActive;
export const selectRoyalCollectActive = (state) =>
  state.featureGame.royalCollectActive;
export const selectHoldAndWinInitialReels = (state) =>
  state.featureGame.initialReels;
export const selectHoldAndWinCurrentRound = (state) =>
  state.featureGame.currentRound;
export const selectHoldAndWinCoinMultipliers = (state) =>
  state.featureGame.currentRound?.storage?.coin_multipliers || null;
export const selectRespinsLeft = (state) => state.featureGame.respinsLeft;
export const selectProgressBarCount = (state) =>
  state.featureGame.progressBarCount;
export const selectHoldAndWinSymbolsReady = (state) =>
  state.featureGame.holdAndWinSymbolsReady;
export const selectHoldAndWinReelsReady = (state) =>
  state.featureGame.holdAndWinReelsReady;
export const selectHoldAndWinOverlayReelsReady = (state) =>
  state.featureGame.holdAndWinOverlayReelsReady;
export const selectIsHoldAndWinIntroReload = (state) =>
  state.featureGame.isHoldAndWinIntroReload;
export const selectIsHoldAndWinMidFeatureReload = (state) =>
  state.featureGame.isHoldAndWinMidFeatureReload;
export const selectIsHoldAndWinCompletionReload = (state) =>
  state.featureGame.isHoldAndWinCompletionReload;
export const selectTriggerRoundWin = (state) =>
  state.featureGame.triggerRound?.win || 0;
export const selectFreeSpinTriggered = (state) =>
  state.featureGame.freeSpinTriggered;
export const selectFreeSpinActive = (state) =>
  state.featureGame.freeSpinActive;
export const selectFreeSpinSpinning = (state) =>
  state.featureGame.freeSpinSpinning;
export const selectFreeSpinComplete = (state) =>
  state.featureGame.freeSpinComplete;
export const selectRoyalCollectTriggered = (state) =>
  state.featureGame.royalCollectTriggered;
export const selectRoyalCollectInitialReels = (state) =>
  state.featureGame.royalCollectInitialReels;
export const selectRoyalCollectCurrentRound = (state) =>
  state.featureGame.royalCollectCurrentRound;
export const selectRoyalCollectRespinsLeft = (state) =>
  state.featureGame.royalCollectRespinsLeft;
export const selectRoyalCollectSymbolsReady = (state) =>
  state.featureGame.royalCollectSymbolsReady;
export const selectRoyalCollectReelsReady = (state) =>
  state.featureGame.royalCollectReelsReady;
export const selectRoyalCollectOverlayReelsReady = (state) =>
  state.featureGame.royalCollectOverlayReelsReady;

// Pot Collection selectors
export const selectPotCollectionActive = (state) =>
  state.potCollection.potCollectionActive;
export const selectPotCollectionTriggered = (state) =>
  state.potCollection.potCollectionTriggered;
export const selectPotCollectionEntryPhase = (state) =>
  state.potCollection.entryPhase;
export const selectPotCollectionRespinsLeft = (state) =>
  state.potCollection.respinsLeft;
export const selectPotCollectionCoinCount = (state) =>
  state.potCollection.coinCount;
export const selectPotCollectionCurrentRound = (state) =>
  state.potCollection.currentRound;
export const selectPotCollectionTriggerRoundWin = (state) =>
  state.potCollection.triggerRound?.win || 0;
export const selectPotCollectionRespinGameType = (state) =>
  state.potCollection.respinGameType;
export const selectPotCollectionScreenProgress = (state) =>
  state.potCollection.screenProgress;
export const selectPotCollectionScreenProgressIndex = (state) =>
  state.potCollection.screenProgressIndex;
export const selectPotCollectionScreenProgressProcessing = (state) =>
  state.potCollection.screenProgressProcessing;
export const selectPotCollectionSymbolsReady = (state) =>
  state.potCollection.symbolsReady;
export const selectPotCollectionReelsReady = (state) =>
  state.potCollection.reelsReady;
export const selectPotCollectionOverlayReelsReady = (state) =>
  state.potCollection.overlayReelsReady;
export const selectPotCollectionReloadPhase = (state) =>
  state.potCollection.reloadPhase;
export const selectPotCollectionInitialRespinScreen = (state) =>
  state.potCollection.initialRespinScreen;
export const selectPotCollectionInitialRespinValues = (state) =>
  state.potCollection.initialRespinValues;
export const selectPotCollectionActivationQueue = (state) =>
  state.potCollection.activationQueue;
export const selectPotCollectionCurrentActivation = (state) =>
  state.potCollection.currentActivation;
export const selectIsPotCollectionMidBonusReload = (state) =>
  state.potCollection.reloadPhase === "respins";

// Feature spin state selectors (generic for any feature with parallel spin cycle)
export const selectFeatureSpinActive = (state) => state.spin.featureSpinActive;
export const selectFeatureSpinComplete = (state) =>
  state.spin.featureSpinComplete;
export const selectFeatureSpinStatus = (state) => state.spin.featureSpinStatus;
export const selectFeatureSpinManualStop = (state) =>
  state.spin.featureSpinManualStop;

// ============================================
// USER INTERFACE SELECTORS
// ============================================
export {
  selectMenuActive,
  selectIntroPopupActive,
  selectUserInterfaceDisabled,
  selectPaytableActive,
  selectGameRulesActive,
  selectSettingsActive,
  selectReplayActive,
  selectReplayEnterActive,
  selectReplayEndedActive,
  selectCreditsPopupActive,
  selectExpirationPopupActive,
  selectBuyFreeSpinsPopupActive,
  selectConnectionPopupActive,
  selectSoundAdjustmentPopupActive,
  selectSymbolPopupActive,
  selectSymbolPopupBlocked,
  selectMiniPaytablePopupActive,
  selectTurboSuggestionPopupActive,
  selectAutoplayPopupActive,
  selectBetSelectPopupActive,
  selectBonusBuyActive,

  selectAnyPopupActive,
  selectHoldAndWinResumePopupActive,
  selectErrorPopupActive,
  selectErrorPopupCode,
  selectInfoPopupActive,
  selectInfoPopupKey,
  selectInfoPopupData,
  selectIosFullscreenOverlayActive,
  selectLeaveGamePopupActive,
} from "./Slices/userInterfaceSlice";

// ============================================
// SETTINGS SELECTORS
// ============================================
export {
  selectAutoPlaySpeed,
  selectAutoPlayActive,
  selectRemainingAutoSpins,
  selectAutoSpinCount,
  selectPendingAutoPlaySpeed,
  selectSimulationModeEnabled,
  selectBuyFreeSpinsMode,
  selectSoundLevelAdjustmentEnabled,
  selectManualStopCount,
  selectManualStopTrackingEnabled,
  selectTurboSuggestionPending,
  selectStopOnAnyWin,
  selectStopOnBonusGame,
  selectStopOnSingleWinExceeds,
  selectStopOnBalanceIncreases,
  selectStopOnBalanceDecreases,
  selectSingleWinExceedsAmount,
  selectBalanceIncreasesAmount,
  selectBalanceDecreasesAmount,
} from "./Slices/settingsSlice";

// ============================================
// ANIMATION SELECTORS
// ============================================
export {
  selectIntroTransitionActive,
  selectShowGameAfterTransition,
  selectErrorRecoveryTransitionActive,
  selectErrorRecoveryScreenCovered,
  selectResetMainGameVisualEffects,
  selectCounterAnimationFinished,
  selectLinesRotationFinished,
  selectWinAnimationsPlaying,
  selectIsAnimatedWin,
  selectCriticalAnimationPlaying,
  selectUserWantsToDismissWinAnimation,
  selectSkipWinAnimation,
  selectIsWaitingForUserConfirmation,
  selectAnimatedWinAmount,
  selectCounterAnimationStarted,
  selectSkipCounterAnimation,
  selectShowHoldAndWinIntro,
  selectSceneTransitionActive,
  selectShowHoldAndWinOutro,
  selectShowHoldAndWinCollector,
  selectShowMaxWin,
  selectExpandingWildsOccurred,
  selectFreeSpinTransitionActive,
  selectFreeSpinTransitionMidpoint,
  selectShowFreeSpinIntro,
  selectShowFreeSpinsReload,
  selectShowAdditionalFreeSpins,
  selectShowFreeSpinMaxWin,
  selectShowFreeSpinTotalWin,
  selectShowFreeSpinZeroWin,
  selectShowTransitionDialog,
  selectShowGambleDialog,
  selectFreeSpinCounterVisible,
  selectShowRoyalCollectIntro,
  selectRoyalCollectTransitionActive,
  selectShowRoyalCollectOutro,
  selectRoyalCollectOutroTransitionActive,
  selectShowPotCollectionIntro,
  selectPotCollectionTransitionActive,
  selectShowPotCollectionOutro,
  selectPotCollectionOutroTransitionActive,
  selectShowPotCollectionMaxWin,
  selectPotCollectionOutroCounterFinished,
} from "./Slices/animationsSlice";

// ============================================
// SOUND SELECTORS
// ============================================
export { selectMasterVolume } from "./Slices/soundsSlice";

// ============================================
// LOADING PROGRESS SELECTORS
// ============================================
export const selectLoaderFadedOut = (state) =>
  state.loadingProgress.loaderFadedOut;
export const selectIntroAssetsReady = (state) =>
  state.loadingProgress.introAssetsReady;
export const selectIntroMounted = (state) =>
  state.loadingProgress.introMounted;
/** Everything loaded — what unlocks tap-to-start on the intro popup. */
export const selectGameFullyLoaded = (state) =>
  state.loadingProgress.status === "success";
/** Init failed — nothing may mount, and the error popup owns the screen. */
export const selectLoadingFailed = (state) =>
  state.loadingProgress.status === "error";
// ============================================
// FREE ROUNDS SELECTORS
// ============================================
export {
  selectFreeRoundsActive,
  selectFreeRoundsCount,
  selectFreeRoundsPerformed,
  selectFreeRoundsTotalWin,
  selectFreeRoundsDisplayedWin,
  selectFreeRoundsWinCounted,
  selectFreeRoundsBet,
  selectFreeRoundsStatus,
  selectFreeRoundsCompletionPending,
  selectFreeRoundsEnded,
  selectFreeRoundsReloadPending,
  selectFreeRoundsReloadWinWithheld,
  // DEPRECATED stubs — will be removed when legacy FR components are rewritten
  selectIsFreeRoundsCompletionReload,
  selectBalanceAlreadyUpdated,
} from "./Slices/freeRoundsSlice";

// ============================================
// REPLAY / HISTORY SELECTORS
// ============================================
export {
  selectReplayRounds,
  selectReplayHistoryStatus,
  selectReplayHistoryError,
  selectReplayHistoryLoading,
  selectReplayHistoryEmpty,
  selectEnterRound,
  selectEndedRound,
  selectReplaySessionActive,
  selectIsStandaloneReplay,
  selectReplayInitError,
  selectReplayBet,
  selectReplayBalance,
  selectNextReplayStepId,
  selectReplayFinished,
  selectReplayRoundId,
} from "./Slices/replaySlice";

// ============================================
// MAD LABS FREE SPINS
// ============================================
// ALL PLURAL. The SINGULAR selectFreeSpin* exports elsewhere in this file are
// featureGameSettingsSlice's legacy Zeus ones, which drive the old mask-reveal
// flow and are read only from primary/. New code wants the plural set.
export {
  selectFreeSpinsTriggered,
  selectFreeSpinsActive,
  selectFreeSpinsSpinning,
  selectFreeSpinsSpinComplete,
  selectFreeSpinsAwarded,
  selectFreeSpinsPlayed,
  selectFreeSpinsPackageTier,
  selectFreeSpinsRemaining,
  selectFreeSpinsRemainingDisplay,
  selectIsLastFreeSpin,
  selectFreeSpinsLevel,
  selectFreeSpinsTier,
  selectFreeSpinsPendingTier,
  selectFreeSpinsPendingServerLevel,
  selectFreeSpinsPendingLevelUpSpins,
  selectFreeSpinsPendingLevelUpSpinsList,
  selectFreeSpinsServerLevel,
  selectFreeSpinsEntrySequencePending,
  selectFreeSpinsAwaitingResume,
  selectFreeSpinsReloadPopupPending,
  selectFreeSpinsCurrentRound,
  selectFreeSpinsWinningLines,
  selectFreeSpinsTotalWin,
  selectFreeSpinsWinLimitReached,
  selectFreeSpinsDisplayedTotalWin,
  selectFreeSpinsTriggerWin,
  selectFreeSpinsTriggerScatterCell,
  selectFreeSpinsLastWin,
  selectGamble,
  selectGambleSettled,
  selectFreeSpinsReloadPhase,
  selectGambleVariants,
  selectGambleCurrent,
  selectCanGamble,
  selectGambleLost,
} from "./Slices/freeSpinsSlice";

// ============================================
// DERIVED: ANY FEATURE ACTIVE
// ============================================
// Single source of truth for "is any feature game running?"
// Use this in UI components instead of importing individual feature selectors.
// When adding a new feature, add its active state here — all UI guards update automatically.
export const selectAnyFeatureActive = (state) =>
  state.featureGame.holdAndWinActive ||
  state.featureGame.royalCollectActive ||
  state.featureGame.freeSpinActive ||
  state.potCollection.potCollectionActive ||
  state.freeSpins.active ||
  !!state.holdAndWin?.active;

// Single source of truth for "is any feature triggered OR active?"
// Covers trigger phase (intro/transition) and active phase for ALL features.
// Use this in autoplay to pause during any feature without per-feature branching.
export const selectAnyFeatureTriggeredOrActive = (state) => {
  // Active features
  if (
    state.featureGame.holdAndWinActive ||
    state.featureGame.royalCollectActive ||
    state.featureGame.freeSpinActive ||
    state.potCollection.potCollectionActive ||
    state.freeSpins.active ||
    state.holdAndWin?.active
  ) return true;

  // Triggered features (intro phase)
  if (state.featureGame.royalCollectTriggered) return true;
  if (state.potCollection.potCollectionTriggered) return true;
  if (state.holdAndWin?.triggered) return true; // Hold & Win, its own slice
  // Covers the whole window from the scatters landing to the round starting:
  // the reveal, the gamble screen, the intro popup and the wipe.
  if (state.freeSpins.triggered) return true;

  // featureState-based triggers (holdAndWin intro, freeSpins intro)
  const fs = state.game.featureState;
  if (fs && fs.phase === "intro") return true;

  // Legacy flowState fallback for hold & win
  if (state.game.lastCommand === "spin" && state.game.flowState === "respin") return true;

  return false;
};
